import {
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { Alert, FormGroup, Input, Label } from "reactstrap";
import BjelinButton from "../BjelinButton";
import classNames from "classnames";
import { debugUser } from "../../utils/DebugUtils";
import ErrorCodes from "../../config/ErrorCodes";
import useIsMounted from "../../hooks/useIsMounted";
import { UserContext } from "../../functional-components/UserContext";
import { gtmSendEvent } from "../../utils/GTMUtils";

export const getInitialLoginData = () => ({
  username: debugUser.email || "",
  password: debugUser.password || ""
});

export type LoginFormProps = {
  className?: string | string[];
  afterLogin?: () => void;
  autoFocusInput: boolean;
  initialValues: { username: string; password: string };
};

const LoginForm = ({
  className,
  afterLogin,
  autoFocusInput = false,
  initialValues
}: LoginFormProps) => {
  const userContext = useContext(UserContext);
  const t = useBjelinTranslations();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState(false);
  const [failedLogin, setFailedLogin] = useState<string | boolean>(false);
  const userNameInput = useRef<HTMLInputElement>(null);
  const passwordInput = useRef<HTMLInputElement>(null);
  const loginData = initialValues || getInitialLoginData();

  useEffect(() => {
    setTimeout(() => {
      if (autoFocusInput && userNameInput.current) {
        userNameInput.current.focus();
      }
    }, 300);
  }, [autoFocusInput]);

  const login = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!loading && userNameInput.current && passwordInput.current) {
        setLoading(true);
        setFailedLogin(false);
        userContext
          ?.signIn(userNameInput.current.value, passwordInput.current.value)
          .then(() => {
            gtmSendEvent({
              event: "login",
              method: "username/password"
            });
            afterLogin?.();
          })
          .catch(data => {
            console.error(data);
            if (isMounted()) {
              setFailedLogin(t(ErrorCodes.LOGIN_FAILED));
            }
          })
          .finally(() => {
            if (isMounted()) {
              setLoading(false);
            }
          });
      }
      return false;
    },
    [afterLogin, isMounted, loading, t, userContext]
  );

  return (
    <form className={classNames("login-form", className)} onSubmit={login}>
      <FormGroup>
        <Label>
          {t("your.email.address")}:
          <Input
            className={"w-100"}
            type={"email"}
            autoComplete={"email"}
            name={"email"}
            placeholder={t("email.address")}
            innerRef={userNameInput}
            defaultValue={loginData?.username}
          />
        </Label>
      </FormGroup>
      <FormGroup>
        <Label>
          {t("your.password")}:
          <Input
            className={"w-100"}
            type={"password"}
            autoComplete={"current-password"}
            name={"password"}
            placeholder={t("password")}
            innerRef={passwordInput}
            defaultValue={loginData?.password}
          />
        </Label>
      </FormGroup>
      {failedLogin && (
        <FormGroup>
          <Alert color={"danger"}>{failedLogin}</Alert>
        </FormGroup>
      )}
      <FormGroup>
        <BjelinButton
          className={"login-btn"}
          loading={loading}
          color={"bjelin-darkgrey"}
        >
          {t("log.in")}
        </BjelinButton>
      </FormGroup>
    </form>
  );
};

export default LoginForm;
